<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PipelinesAPI from 'dashboard/api/pipelines';
import KanbanView from './KanbanView.vue';
import NewPipelineDialog from '../components/NewPipelineDialog.vue';

const route = useRoute();
const router = useRouter();

const pipelines = ref([]);
const loading = ref(true);
const error = ref(null);
const showNewDialog = ref(false);

const activePipelineId = computed(() => Number(route.params.pipelineId) || null);
const activePipeline = computed(() =>
  pipelines.value.find(p => p.id === activePipelineId.value)
);

// Recordar el último embudo visitado por cuenta, para volver a él al regresar a
// Leads desde el chat o tras dar atrás en un cliente (Victor 2026-05-26).
const lastPipelineKey = () => `leads_last_pipeline_${route.params.accountId}`;
function rememberPipeline(id) {
  try { window.localStorage.setItem(lastPipelineKey(), String(id)); } catch (_) { /* noop */ }
}
function recallPipeline() {
  try { return Number(window.localStorage.getItem(lastPipelineKey())) || null; } catch (_) { return null; }
}

async function fetchPipelines() {
  loading.value = true;
  error.value = null;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data.payload || [];
    if (!activePipelineId.value && pipelines.value.length > 0) {
      // Al volver a Leads, abrir el último embudo visitado (si aún existe);
      // si no, el DTO. DE VENTAS por nombre; si no, el primero.
      const lastId = recallPipeline();
      const target =
        pipelines.value.find(p => p.id === lastId) ||
        pipelines.value.find(p => /ventas/i.test(p.name || '')) ||
        pipelines.value[0];
      goToPipeline(target);
    }
  } catch (e) {
    error.value = e.response?.status ? `HTTP ${e.response.status}` : e.message;
  } finally {
    loading.value = false;
  }
}

function goToPipeline(pipeline) {
  router.push({
    name: 'pipelines_view',
    params: { accountId: route.params.accountId, pipelineId: pipeline.id },
  });
}

function onPipelineCreated(pipeline) {
  showNewDialog.value = false;
  pipelines.value.push(pipeline);
  goToPipeline(pipeline);
}

function totalLeadsForPipeline(pipeline) {
  // El backend devuelve sólo stages aquí, no el conteo de deals.
  // Mostramos el número de etapas como hint mientras tanto.
  return pipeline.stages?.length || 0;
}

onMounted(fetchPipelines);
watch(activePipelineId, id => {
  // Por si llegan a la URL directa sin pasar por home
  if (!pipelines.value.length) fetchPipelines();
  // Recordar el embudo activo para restaurarlo al regresar a Leads
  if (id) rememberPipeline(id);
}, { immediate: true });
</script>

<template>
  <div class="flex flex-1 w-full min-w-0 h-full bg-slate-50 dark:bg-slate-900 overflow-hidden">
    <!-- Sidebar Departamentos eliminado · ahora se cambia desde el dropdown del título "Embudo de ventas ▾" en KanbanView -->

    <main class="flex-1 overflow-hidden min-w-0">
      <KanbanView
        v-if="activePipelineId"
        :key="activePipelineId"
      />
      <div v-else-if="!loading" class="h-full flex items-center justify-center text-center px-6">
        <div>
          <div class="text-5xl mb-3">📋</div>
          <div class="text-slate-700 dark:text-slate-200 font-medium text-base">
            Selecciona un embudo a la izquierda
          </div>
          <div class="text-sm text-slate-500 mt-1 mb-4">
            o crea uno nuevo
          </div>
          <button
            @click="showNewDialog = true"
            class="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-md"
          >
            + Agregar embudo
          </button>
        </div>
      </div>
    </main>

    <NewPipelineDialog
      v-if="showNewDialog"
      :account-id="route.params.accountId"
      @close="showNewDialog = false"
      @created="onPipelineCreated"
    />
  </div>
</template>
